<template lang="pug">
router-link(
  v-if="twin",
  :to="{ name: twinLinkName, params: { template: twin.templateUuid, id: twin.uuid } }"
)
  .flex.flex-row.gap-3.border.border-gray-200.rounded-lg.p-3.bg-white(
    v-loading="loading"
  )
    img.w-40.h-40.object-cover.rounded.shrink-0(
      v-if="primaryImageUrl",
      :src="primaryImageUrl"
    )
    .flex.w-40.h-40.shrink-0.items-center.justify-center.rounded.bg-gray-100(
      v-else
    )
      el-icon(size="48", color="var(--el-border-color)")
        Camera
    .grid.grid-cols-2.items-baseline.gap-x-4.gap-y-1.content-start
      span.text-sm.font-medium {{ $t("name") }}
      span.text-sm {{ twin.name }}
      template(v-for="prop in topProperties", :key="prop.uuid")
        span.text-sm.font-medium {{ prop.name }}
        TwinPropertyView(:prop="prop")
span(v-else) -
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { getTwin, type TwinResponse } from "@shared/services/twinManagement";
import { useTwinDocumentsStore } from "@shared/stores/documents";
import TwinPropertyView from "@shared/components/twinManagement/components/TwinPropertyView.vue";
import { useAppStore } from "@shared/stores/apps";

const props = defineProps<{
  value?: Array<{ uuid: string; name: string }>;
}>();

const documentsStore = useTwinDocumentsStore();
const appStore = useAppStore();

const twin = ref<TwinResponse | null>(null);
const loading = ref(false);

const twinId = computed(() => props.value?.[0]?.uuid);

const twinLinkName = computed(() => {
  return appStore.getLink(
    `twin-edit.${twin.value?.type}.${twin.value?.templateUuid}.${twin.value?.uuid}`,
  )?.routeName;
});

async function load(id: string | undefined) {
  if (!id) {
    twin.value = null;
    return;
  }
  loading.value = true;
  try {
    const [twinResponse] = await Promise.all([
      getTwin(id),
      documentsStore.loadTwinImages(id, false),
    ]);
    twin.value = twinResponse.data;
  } finally {
    loading.value = false;
  }
}

onMounted(() => load(twinId.value));
watch(twinId, load);

const primaryImageUrl = computed(() => {
  if (!twinId.value) return null;
  const images =
    documentsStore.getTwinImageLoadingState(twinId.value).result ?? [];
  const sorted = [...images].sort((a, b) =>
    a.purpose === "primary" ? -1 : b.purpose === "primary" ? 1 : 0,
  );
  return sorted[0] ? `/api/documents/${sorted[0].uuid}` : null;
});

const topProperties = computed(() => {
  return (twin.value?.properties ?? [])
    .filter((p) => p.isUserRelevant)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .slice(0, 5);
});
</script>
